import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { buildMonthlyDelta, MonthlyDelta } from 'app/shared/utils/monthly-delta';
import { DateTime } from 'luxon';

/**
 * Named-palette → hex map (mirrors brandbot's workspace-stat-card). The card
 * accent is driven by a CSS variable (`--icon-accent`) instead of dynamic
 * `text-{color}-500` / `bg-{color}-500` classes — those never compiled (Tailwind
 * can't see runtime-interpolated class names), which is why the icons rendered
 * as solid colored squares with no visible glyph.
 */
export const PALETTE: Record<string, string> = {
  /* The brand accent follows the user preference (defaults to sapphire) —
     safe here because `--icon-accent` is only ever consumed as a CSS color. */
  sapphire: 'var(--pc-accent-base)',
  indigo: '#6366f1',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  violet: '#8b5cf6',
  teal: '#14b8a6',
  emerald: '#10b981',
  slate: '#64748b',
  green: '#22c55e',
  red: '#ef4444',
  orange: '#f97316',
  pink: '#ec4899',
  gray: '#6b7280',
};

@Component({
  selector: 'primecontrol-metric-card',
  imports: [SvgIcon, TranslateModule],
  templateUrl: './metric-card.html',
  styleUrls: ['./metric-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricCard {
  readonly metric = input.required<any>();

  private _translateService: TranslateService = inject(TranslateService);

  /** Recompute the delta's month names when the catalog loads or the user
   *  switches language — the metric object itself doesn't change on either. */
  private _langChange = toSignal(this._translateService.onLangChange);

  /** Resolved hex accent for the icon chip; falls back to the theme primary. */
  readonly accentHex = computed<string>(
    () => PALETTE[this.metric()?.color ?? ''] ?? 'var(--p-primary-color)'
  );

  /** Total shown in the card, counted up from 0 on load (see constructor). */
  readonly displayTotal = signal(0);

  /** Last animated target — guards the count-up against re-running when the
   *  parent re-emits an equivalent metric object (identity change, same value). */
  private _lastTarget: number | null = null;

  constructor() {
    // Count-up: the total eases from 0 to its value in ~600ms. tabular-nums
    // in the template keeps the layout still while digits change. Skipped
    // under prefers-reduced-motion.
    effect((onCleanup) => {
      const target = Number(this.metric()?.total ?? 0) || 0;
      if (target === this._lastTarget) {
        return;
      }
      this._lastTarget = target;
      const reducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || target <= 0) {
        this.displayTotal.set(target);
        return;
      }
      const duration = 600;
      const start = performance.now();
      let frame = 0;
      const tick = (now: number): void => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.displayTotal.set(Math.round(target * eased));
        if (progress < 1) {
          frame = requestAnimationFrame(tick);
        }
      };
      frame = requestAnimationFrame(tick);
      onCleanup(() => cancelAnimationFrame(frame));
    });
  }

  /**
   * Trend sparkline (inline SVG area + line, tinted with the card accent) drawn
   * from `metric.series` — the endpoint's monthly created-records buckets. Null
   * when the series is missing or too short to plot: the card used to fall back
   * to a title-seeded placeholder curve, which drew a trend the instance never
   * had. An absent line is honest; an invented one isn't.
   */
  readonly sparkline = computed<{
    line: string;
    area: string;
    points: { x: number; y: number; value: number }[];
  } | null>(() => {
    const series = this.metric()?.series;
    if (!Array.isArray(series) || series.length < 2) {
      return null;
    }
    const raw: number[] = series.map(Number).map((v: number) => (Number.isFinite(v) ? v : 0));

    const width = 100;
    const height = 32;
    const pad = 3;
    const min = Math.min(...raw);
    const max = Math.max(...raw);
    const range = max - min || 1;
    const step = width / (raw.length - 1);
    const points = raw.map((value, i) => ({
      x: +(i * step).toFixed(2),
      y: +(pad + (height - 2 * pad) * (1 - (value - min) / range)).toFixed(2),
      value,
    }));

    const line = points.map((p) => `${p.x},${p.y}`).join(' ');
    const area = `M0,${height} L${points.map((p) => `${p.x},${p.y}`).join(' L')} L${width},${height} Z`;
    return { line, area, points };
  });

  /** Index of the month under the pointer on the sparkline; null when not hovering. */
  readonly hoverIndex = signal<number | null>(null);

  /**
   * The hovered month as the reader needs it: its name, its count and where to
   * draw the marker (percent of the sparkline box, which is a 100×32 viewBox).
   * Without it the line had no scale and no months, so nothing on it could be read.
   */
  readonly hoveredPoint = computed<{
    label: string;
    value: number;
    left: number;
    top: number;
  } | null>(() => {
    const index = this.hoverIndex();
    const spark = this.sparkline();
    if (index === null || !spark) return null;
    this._langChange();
    const point = spark.points[index];
    const monthsAgo = spark.points.length - 1 - index;
    const lang = this._translateService.currentLang || this._translateService.defaultLang || 'es';
    return {
      label: DateTime.now().minus({ months: monthsAgo }).setLocale(lang).toFormat('LLL yyyy'),
      value: point.value,
      left: point.x,
      top: (point.y / 32) * 100,
    };
  });

  /** Snaps the pointer to the nearest month bucket. */
  onSparkMove(event: MouseEvent): void {
    const spark = this.sparkline();
    if (!spark) return;
    const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    this.hoverIndex.set(Math.round(ratio * (spark.points.length - 1)));
  }

  /** New records this month vs last, as an absolute count — see
   *  `buildMonthlyDelta` for why this replaced the ±100% percentage badge. */
  readonly delta = computed<MonthlyDelta | null>(() => {
    this._langChange();
    return buildMonthlyDelta(
      this.metric()?.series,
      this._translateService.currentLang || this._translateService.defaultLang || 'es'
    );
  });
}
